"use client";

import { Button } from "@restomat/components/client/ui";
import { LANDING_CONTAINER_CLASS } from "@/components/landing/constants";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { trackClickTryOut } from "@/lib/analytics";
import { motion } from "framer-motion";
import { fadeInUp, staggerContainer, scaleIn } from "./animations";
import { Check, Play } from "lucide-react";

interface AdLandingTemplateProps {
  headline: string;
  subheadline: string;
  ctaText: string;
  features: string[];
  testimonial?: {
    quote: string;
    name: string;
    role: string;
  };
  videoPlaceholder?: boolean;
}

export default function AdLandingTemplate({
  headline,
  subheadline,
  ctaText,
  features,
  testimonial,
  videoPlaceholder = true,
}: AdLandingTemplateProps) {
  const t = useTranslations("landing");
  return (
    <main className="flex flex-col w-full bg-background text-foreground min-h-screen">
      <section className="py-12 md:py-20">
        <motion.div
          className={`${LANDING_CONTAINER_CLASS} space-y-12`}
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div className="flex flex-col gap-6" variants={fadeInUp}>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight">
                {headline}
              </h1>
              <p className="text-lg text-muted-foreground">{subheadline}</p>

              <ul className="flex flex-col gap-3 mt-4">
                {features.map((feature) => (
                  <li key={feature} className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-1">
                      <Check className="h-5 w-5 text-secondary" />
                    </div>
                    <span className="text-foreground">{feature}</span>
                  </li>
                ))}
              </ul>

              <motion.div
                className="flex flex-col gap-2 mt-6"
                variants={scaleIn}
              >
                <Button
                  className="uppercase w-full sm:w-auto"
                  size="lg"
                  asChild
                  onClick={() => trackClickTryOut()}
                >
                  <Link href="/auth/login?screen_hint=signup" prefetch={false}>{ctaText}</Link>
                </Button>
                <span className="text-sm text-tertiary mt-2">
                  {t("cta.noCardRegisterMinute")}
                </span>
              </motion.div>
            </motion.div>

            {videoPlaceholder && (
              <motion.div
                className="relative aspect-video rounded-2xl overflow-hidden border-4 border-foreground bg-muted"
                variants={fadeInUp}
              >
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="flex flex-col items-center gap-4 text-center p-8">
                    <div
                      className="w-20 h-20 rounded-full bg-primary flex items-center justify-center cursor-pointer hover:scale-105 transition-transform"
                      role="button"
                      aria-label={t("adLanding.playDemoAria")}
                      tabIndex={0}
                    >
                      <Play className="h-10 w-10 text-primary-foreground ml-1" />
                    </div>
                    <p className="text-muted-foreground text-sm">
                      {t("adLanding.demoComingSoon")}
                    </p>
                  </div>
                </div>
              </motion.div>
            )}
          </div>

          {testimonial && (
            <motion.div
              className="mt-16 p-8 rounded-2xl border-2 border-foreground bg-card"
              variants={fadeInUp}
            >
              <div className="flex flex-col md:flex-row items-center gap-6">
                <div className="flex-shrink-0 w-16 h-16 rounded-full bg-tertiary border-2 border-foreground flex items-center justify-center">
                  <span className="text-2xl font-bold text-foreground">
                    {testimonial.name.charAt(0)}
                  </span>
                </div>
                <div className="flex flex-col gap-2 text-center md:text-left">
                  <blockquote className="italic text-foreground">
                    &ldquo;{testimonial.quote}&rdquo;
                  </blockquote>
                  <div className="flex flex-col md:flex-row items-center gap-2">
                    <span className="font-bold text-foreground">
                      {testimonial.name}
                    </span>
                    <span className="hidden md:inline text-muted-foreground">
                      •
                    </span>
                    <span className="text-muted-foreground">
                      {testimonial.role}
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          <motion.div
            className="flex flex-col items-center gap-3 pt-8 border-t border-foreground/10"
            variants={fadeInUp}
          >
            <p className="text-muted-foreground text-center">
              {t("adLanding.joinRestaurants")}
            </p>
            <Button
              className="uppercase"
              size="lg"
              asChild
              onClick={() => trackClickTryOut()}
            >
              <Link href="/auth/login?screen_hint=signup" prefetch={false}>{ctaText}</Link>
            </Button>
            <span className="text-sm text-muted-foreground mt-2 text-tertiary">
              {t("cta.noCardRegisterMinute")}
            </span>
          </motion.div>
        </motion.div>
      </section>
    </main>
  );
}
